import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ReusableTable from "../../common/table/CommonTable";
import { deleteDoctor, fetchDoctors } from "../../redux/doctor/doctorAction";

const ListDoctor = () => {
  const [loading, setLoading] = useState(false);
  const [openDeleteModel, setOpenDeleteModel] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const searchTimeoutRef = useRef(null);

  const DoctorState = useSelector(
    (state) => state?.doctor?.fetchListDoctors || {}
  );
  const deleteDoctorState = useSelector(
    (state) => state.doctor.deleteDoctor || {}
  );
  const totalPages = DoctorState?.data?.totalPages || 0;

  const fetchData = async (page, search = "") => {
    setLoading(true);
    const payload = {
      pageNo: page,
      pagesize: rowsPerPage,
      pagination_required: true,
      search: search,
      search_by: "id,name,email,phone",
    };
    await dispatch(fetchDoctors(payload));
    setLoading(false);
  };

  useEffect(() => {
    fetchData(currentPage, searchTerm);
  }, [currentPage, dispatch]);

  useEffect(() => {
    if (searchTerm !== "") {
      setCurrentPage(1);
      fetchData(1, searchTerm);
    } else {
      fetchData(currentPage, "");
    }
  }, [searchTerm]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchInputValue(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(value);
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const rawData = DoctorState?.data?.data || [];
  const isNoRecordsFound = DoctorState?.message === "No records found";
  const lowercasedSearchTerm = searchTerm.toLowerCase();
  const filteredData = useMemo(() => {
    if (isNoRecordsFound) return [];

    const formattedData = Array.isArray(rawData)
      ? rawData.map((doc) => ({
          ids: doc.id || "N/A",
          id: doc.doctor_id || "N/A",
          name: doc.doctor_name || "N/A",
          email: doc.email || "N/A",
          phone: doc.phone || "N/A",
          status: doc.User?.status === 1 ? "Active" : "Inactive",
        }))
      : [];

    return searchTerm
      ? formattedData.filter(
          (doctor) =>
            doctor.id.toString().includes(lowercasedSearchTerm) ||
            doctor.name.toLowerCase().includes(lowercasedSearchTerm) ||
            doctor.email.toLowerCase().includes(lowercasedSearchTerm) ||
            doctor.phone.toString().includes(lowercasedSearchTerm)
        )
      : formattedData;
  }, [rawData, searchTerm, isNoRecordsFound]);

  const columns = [
    {
      id: "id",
      label: "User ID",
      sortable: true,
      render: (value, row) => (
        <Typography
          component="span"
          sx={{
            cursor: "pointer",
            color: "dodgerblue",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
          onClick={() => handleViewClick(row)}
        >
          {value}
        </Typography>
      ),
    },
    { id: "name", label: "Name", sortable: true },
    { id: "email", label: "Email", sortable: true },
    { id: "phone", label: "Phone Number", sortable: true },
    {
      id: "status",
      label: "Status",
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          color={value === "Active" ? "success" : "warning"}
        />
      ),
    },
  ];

  const handleViewClick = (row) => {
    navigate(`/doctor/view/${row.ids}`);
  };

  const handleEditClick = (row) => {
    navigate(`/doctor/edit/${row.ids}`);
  };

  const handleDeleteClick = (row) => {
    console.log("Delete clicked for row:", row);
    console.log("Row IDs:", row.ids);
    setDeleteId(row.ids);
    setOpenDeleteModel(true);
  };

  const handleDeleteConfirm = async () => {
    console.log("Deleting ID:", deleteId);
    if (deleteId) {
      try {
        await dispatch(deleteDoctor(deleteId));
        setOpenDeleteModel(false);
        await fetchData(currentPage, searchTerm);
        setDeleteId(null);
      } catch (error) {
        console.error("Error deleting doctor:", error);
      }
    } else {
      console.error("No delete ID available");
    }
  };

  const handleDeleteCancel = () => {
    setDeleteId(null);
    setOpenDeleteModel(false);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search doctors by id, name, email or phone..."
          value={searchInputValue}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          variant="outlined"
          size="small"
        />
      </Box>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : filteredData.length === 0 ? (
        <Typography>No data found</Typography>
      ) : (
        <ReusableTable
          title="Doctors"
          columns={columns}
          data={filteredData}
          isLoading={loading}
          headerColor="#000"
          rowColor="#ffffff"
          alternateRowColor="#f5f5f5"
          onViewClick={handleViewClick}
          onEditClick={handleEditClick}
          onDeleteClick={handleDeleteClick}
          withPagination={true}
          withActions={true}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          showDownloadOption={true}
        />
      )}

      <Modal
        open={openDeleteModel}
        style={{ left: "35%", top: "35%" }}
        onClose={handleDeleteCancel}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            width: "500px",
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
            padding: "30px",
            borderRadius: "12px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
            border: "1px solid #e0e0e0",
          }}
        >
          <Typography
            id="modal-modal-title"
            variant="h5"
            sx={{
              fontSize: "1.4rem",
              fontWeight: "600",
              color: "#d32f2f",
              marginBottom: "10px",
              textAlign: "center",
            }}
            component="h2"
          >
            ⚠️ Delete Doctor
          </Typography>
          <Typography
            id="modal-modal-description"
            sx={{
              mt: 2,
              mb: 3,
              fontSize: "1rem",
              color: "#424242",
              textAlign: "center",
              lineHeight: 1.6,
            }}
          >
            Are you sure you want to delete this doctor?
            <br />
            <strong>This action cannot be undone.</strong>
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 3,
              mt: 4,
            }}
          >
            <Button
              variant="contained"
              onClick={handleDeleteConfirm}
              disabled={loading}
              sx={{
                backgroundColor: "#d32f2f",
                color: "white",
                padding: "10px 25px",
                borderRadius: "8px",
                fontWeight: "600",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#b71c1c",
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(211,47,47,0.4)",
                },
                "&:disabled": {
                  backgroundColor: "#bdbdbd",
                  color: "#ffffff",
                },
                transition: "all 0.3s ease",
              }}
            >
              {loading ? "Deleting..." : "Delete"}
            </Button>
            <Button
              variant="outlined"
              onClick={handleDeleteCancel}
              disabled={loading}
              sx={{
                borderColor: "#757575",
                color: "#757575",
                padding: "10px 25px",
                borderRadius: "8px",
                fontWeight: "600",
                textTransform: "none",
                "&:hover": {
                  borderColor: "#424242",
                  color: "#424242",
                  backgroundColor: "rgba(0,0,0,0.04)",
                  transform: "translateY(-2px)",
                },
                "&:disabled": {
                  borderColor: "#bdbdbd",
                  color: "#bdbdbd",
                },
                transition: "all 0.3s ease",
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ListDoctor;
